import { useIsoLayoutEffect } from "shared/lib/react/useIsoLayoutEffect"

import { useArrayState } from "shared/lib/useArrayState"
import { useEvent } from "shared/lib/useEvent"
import { DropHandler } from "shared/types/DragAndDrop"
import { TreeNode } from "shared/types/TreeNode"
import { WithID } from "shared/types/WithID"

import { produce } from "immer"

import { exists } from "./exists"
import { hasChildren } from "./hasChildren"

function findChild<T extends WithID>(
  id: string | null,
  children: TreeNode<T>[]
): T | null {
  if (!id) return null
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  return children.reduce((prev: TreeNode<T> | null, item: TreeNode<T>) => {
    if (item.id === id) {
      return item as TreeNode<T>
    }
    if (hasChildren(item)) {
      return findChild(id, item.children || []) || prev
    }
    return prev as T
  }, null)
}

export function useLocalTable<T extends WithID>({
  items
}: {
  items: TreeNode<T>[]
}) {
  const { array, move, setArray } = useArrayState(items)

  useIsoLayoutEffect(() => {
    setArray(items)
  }, [items])

  const onItemPositionChange: DropHandler = useEvent(
    (itemID, prev, prevParent, next, nextParent) => {
      if (prevParent === null && nextParent === null) {
        const found = array.find(item => item.id === itemID)
        if (found) {
          move(found, prev, next)
        }
      } else {
        const topLevel = { id: "table", children: array }
        setArray(
          produce(topLevel, nextTopLevel => {
            const resolvedPrevParent =
              findChild(prevParent, nextTopLevel.children) || nextTopLevel
            const resolvedNextParent =
              findChild(nextParent, nextTopLevel.children) || nextTopLevel

            if (
              hasChildren(resolvedPrevParent) &&
              hasChildren(resolvedNextParent)
            ) {
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              let deletedItems: any[] = []
              if (exists(prev)) {
                deletedItems =
                  resolvedPrevParent.children?.splice(prev, 1) || []
              }
              if (exists(next) && deletedItems[0]) {
                resolvedNextParent.children?.splice(next, 0, deletedItems[0])
              }
            }
          }).children
        )
      }
    }
  )

  return { onItemPositionChange, items: array }
}
